<template>
  <div class="presentations-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="presentations-container">
      <!-- Header Section -->
      <div class="presentations-header">
        <div class="header-content">
          <h1 class="page-title">
            <i class="fas fa-cubes title-icon"></i>
            Módulos do Sistema
          </h1>
          <p class="page-subtitle">Visualize os planos e módulos disponíveis</p>
        </div>
      </div>

      <!-- Content Section -->
      <div class="presentations-content">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <!-- No Results State -->
          <div
            v-show="presentations.length == 0 && !loading"
            class="no-results"
          >
            <i class="fas fa-cube no-results-icon"></i>
            <h4 class="no-results-title">Nenhum plano encontrado</h4>
            <p class="no-results-text">Não há planos disponíveis no momento</p>
          </div>

          <!-- Plans Grid -->
          <div v-show="presentations.length > 0" class="presentations-grid">
            <div
              v-for="presentation in presentations"
              :key="presentation.id"
              class="presentation-card"
            >
              <!-- Logo Section -->
              <div class="card-logo-section" v-if="logo">
                <img :src="logo" alt="Sistema Clin" class="logo" />
              </div>

              <!-- Plan Header -->
              <div class="plan-header">
                <div class="plan-icon">
                  <i class="fas fa-layer-group"></i>
                </div>
                <div class="plan-info">
                  <h3 class="plan-title">{{ presentation.name }}</h3>
                </div>
              </div>

              <!-- Modules List -->
              <div
                class="modules-section"
                v-if="presentation.modules.length > 0"
              >
                <div class="modules-list">
                  <div
                    v-for="(module, key) in presentation.modules"
                    :key="key"
                    class="module-item"
                    :class="{
                      'module-expanded': isModuleExpanded(presentation.id, key),
                    }"
                  >
                    <div
                      class="module-header"
                      @click="toggleModule(presentation.id, key)"
                    >
                      <div class="module-icon-wrapper">
                        <div class="module-icon">
                          <i class="fas fa-check"></i>
                        </div>
                      </div>
                      <div class="module-header-content">
                        <h5 class="module-name">{{ module.name }}</h5>
                        <div class="expand-icon">
                          <i
                            class="fas fa-chevron-down"
                            :class="{
                              rotated: isModuleExpanded(presentation.id, key),
                            }"
                          ></i>
                        </div>
                      </div>
                    </div>

                    <transition name="expand">
                      <div
                        v-if="isModuleExpanded(presentation.id, key)"
                        class="module-details"
                      >
                        <div v-if="module.description" class="module-resources">
                          <div
                            v-for="(resource, index) in getResources(
                              module.description
                            )"
                            :key="index"
                            class="resource-item"
                          >
                            <div class="resource-bullet">
                              <i class="fas fa-check"></i>
                            </div>
                            <span class="resource-text">{{ resource }}</span>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>

              <!-- WhatsApp Section -->
              <div
                class="presentation-whatsapp"
                @click.stop="openWhatsApp(presentation)"
              >
                <div class="whatsapp-icon">
                  <i class="fas fa-comment"></i>
                </div>
                <span class="whatsapp-text">
                  Fale conosco sobre este plano
                </span>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import api from "../services/api";
import { userData } from "../lib/functions";

export default {
  name: "Modules",
  components: {
    NavBar,
    AuthBySessionExpired,
  },
  data() {
    return {
      logo: null,
      presentations: [],
      loading: false,
      expandedModules: {},
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    async retrieve() {
      try {
        let { logo } = userData();
        this.logo = logo;
        this.loading = true;
        const response = await api.get("/presentations");
        const { presentations } = response.data;
        this.presentations = presentations;
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },

    getResources(description) {
      if (!description) return [];
      return description
        .split("\n")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },

    toggleModule(presentationId, moduleIndex) {
      const key = `${presentationId}-${moduleIndex}`;
      this.$set(this.expandedModules, key, !this.expandedModules[key]);
    },

    isModuleExpanded(presentationId, moduleIndex) {
      const key = `${presentationId}-${moduleIndex}`;
      return !!this.expandedModules[key];
    },

    openWhatsApp(presentation) {
      const message = `Olá, quero saber mais sobre o plano ${presentation.name}`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/5511960671262?text=${encodedMessage}`;
      window.open(whatsappUrl, "_blank");
    },
  },
};
</script>

<style scoped>
.presentations-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.presentations-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.presentations-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  text-align: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 8px 0 0 0;
  font-size: 16px;
}

/* Content Section */
.presentations-content {
  margin-bottom: 32px;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 400px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 80px 24px;
  color: #6b7280;
}

.no-results-icon {
  font-size: 64px;
  color: #d1d5db;
  margin-bottom: 24px;
}

.no-results-title {
  color: #1e293b;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.no-results-text {
  color: #6b7280;
  font-size: 16px;
  margin: 0;
}

/* Plans Grid */
.presentations-grid {
  display: flex;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
}

.presentation-card {
  width: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  overflow: hidden;
}

.presentation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 35px rgba(15, 101, 176, 0.15);
  border-color: rgba(15, 101, 176, 0.3);
}

/* Logo Section */
.card-logo-section {
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo {
  width: 200px;
  height: 50px;
  object-fit: contain;
}

/* Plan Header */
.plan-header {
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-bottom: 2px solid #93c5fd;
}

.plan-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3);
}

.plan-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.plan-title {
  font-size: 25px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.plan-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(15, 101, 176, 0.3);
}

/* Modules Section */
.modules-section {
  padding: 24px;
  flex: 1;
}

.modules-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.modules-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modules-title i {
  color: #0f65b0;
}

/* Modules List */
.modules-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.module-item {
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s ease;
}

.module-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.module-item.module-expanded {
  background: #ffffff;
  border-color: #0f65b0;
  box-shadow: 0 4px 12px rgba(15, 101, 176, 0.1);
}

.module-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.module-header:hover {
  background: #f1f5f9;
}

.module-item.module-expanded .module-header {
  background: transparent;
  border-bottom: 1px solid #e5e7eb;
}

.module-icon-wrapper {
  flex-shrink: 0;
}

.module-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  transition: all 0.3s ease;
}

.module-item.module-expanded .module-icon {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  box-shadow: 0 2px 8px rgba(15, 101, 176, 0.3);
}

.module-header-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.module-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  font-family: "WorkSans-SemiBold", sans-serif;
  line-height: 1.4;
  flex: 1;
}

.expand-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  transition: all 0.3s ease;
}

.expand-icon i {
  transition: transform 0.3s ease;
}

.expand-icon i.rotated {
  transform: rotate(180deg);
}

.module-item.module-expanded .expand-icon {
  color: #0f65b0;
}

.module-details {
  padding: 0 16px 16px 60px;
}

.module-description {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

/* Transição de expansão */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 500px;
  opacity: 1;
}

.module-resources {
  display: flex;
  flex-direction: column;
}

.resource-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.resource-bullet {
  color: #7cae66;
  font-size: 12px;
  margin-top: 2px;
  flex-shrink: 0;
}

.resource-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  flex: 1;
}

/* WhatsApp Section */
.presentation-whatsapp {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  border-top: 2px solid #86efac;
  cursor: pointer;
  transition: all 0.3s ease;
}

.presentation-whatsapp:hover {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.4);
}

.whatsapp-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
}

.presentation-whatsapp:hover .whatsapp-icon {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.whatsapp-text {
  font-size: 14px;
  font-weight: 600;
  color: #059669;
  flex: 1;
  text-align: left;
  letter-spacing: 0.5px;
  transition: color 0.3s ease;
}

.presentation-whatsapp:hover .whatsapp-text {
  color: white;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .presentations-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .presentations-container {
    padding: 0 16px;
  }

  .presentations-header {
    padding: 20px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .presentations-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .plan-header {
    padding: 20px;
  }

  .plan-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }

  .plan-title {
    font-size: 18px;
  }

  .modules-section {
    padding: 20px;
  }

  .module-item {
    padding: 12px;
  }

  .presentation-whatsapp {
    padding: 14px 20px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }

  .presentations-grid {
    gap: 12px;
  }

  .card-logo-section {
    padding: 16px;
  }

  .logo {
    width: 80px;
    height: 40px;
  }

  .plan-header {
    padding: 16px;
    flex-direction: column;
    text-align: center;
  }

  .plan-icon {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .plan-info {
    align-items: center;
  }

  .plan-title {
    font-size: 16px;
    text-align: center;
  }

  .modules-section {
    padding: 16px;
  }

  .modules-title {
    font-size: 13px;
  }

  .module-item {
    padding: 12px;
  }

  .module-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .module-name {
    font-size: 14px;
  }

  .module-description {
    font-size: 12px;
  }

  .presentation-whatsapp {
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    text-align: center;
  }

  .whatsapp-icon {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }

  .whatsapp-text {
    font-size: 12px;
    text-align: center;
  }

  .module-header {
    padding: 12px;
  }

  .module-details {
    padding: 0 12px 12px 44px;
  }

  .module-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .module-name {
    font-size: 14px;
  }

  .module-description {
    font-size: 12px;
  }

  .expand-icon {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.presentation-card {
  animation: slideInUp 0.3s ease forwards;
  opacity: 0;
}

.presentation-card:nth-child(1) {
  animation-delay: 0.1s;
}
.presentation-card:nth-child(2) {
  animation-delay: 0.2s;
}
.presentation-card:nth-child(3) {
  animation-delay: 0.3s;
}
.presentation-card:nth-child(4) {
  animation-delay: 0.4s;
}
.presentation-card:nth-child(5) {
  animation-delay: 0.5s;
}
.presentation-card:nth-child(6) {
  animation-delay: 0.6s;
}
</style>